import { AppLayout } from './AppLayout';
import { TopBarBackButton } from './topbar/TopBarBackButton';
import { DesktopPage } from '@/components/desktop/DesktopPage';
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader';
import { useIsDesktop } from '@/components/ui/use-mobile';
import { DetailSkeleton } from './ui/skeleton-presets';
import { useNotification, useMarkNotificationAsRead } from '../lib/convex-hooks';
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember';
import type { Id } from '@pronteer/convex/dataModel';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from './ui/button';
import { parseNotificationLink } from '@/lib/notificationLink';

interface NotificationDetailProps {
  notificationId: Id<'notifications'>;
}

export function NotificationDetail({ notificationId }: NotificationDetailProps) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { data: currentUser } = useCurrentCrewMember();
  const notification = useNotification(notificationId, currentUser?.id);
  const markAsRead = useMarkNotificationAsRead();

  const loading = notification === undefined;

  // Mark as read when viewing
  useEffect(() => {
    if (notification && !notification.isRead && currentUser?.id) {
      markAsRead({ notificationId, crewId: currentUser.id });
    }
  }, [notification, notificationId, markAsRead, currentUser?.id]);

  const formatDateTime = (timestamp: number) => {
    return dayjs(timestamp).format('MMMM D, YYYY [at] h:mm A');
  };

  const desktopHeader = (
    <DesktopPageHeader
      title="Notification"
      breadcrumb={
        <span>
          <Link to="/inbox" className="hover:underline">
            Inbox
          </Link>
          <span className="text-content-primary-subtle"> / Notification</span>
        </span>
      }
    />
  );

  if (loading) {
    if (isDesktop) {
      return (
        <DesktopPage>
          {desktopHeader}
          <DetailSkeleton rows={4} />
        </DesktopPage>
      );
    }
    return (
      <AppLayout
        topBar={<TopBarBackButton title="Notification" />}
        wrapperClassName="bg-surface-L0"
        className="py-4"
      >
        <DetailSkeleton rows={4} />
      </AppLayout>
    );
  }

  if (!notification) {
    const notFound = (
      <p className="text-content-primary-subtle text-sm">Notification not found</p>
    );
    if (isDesktop) {
      return (
        <DesktopPage>
          {desktopHeader}
          {notFound}
        </DesktopPage>
      );
    }
    return (
      <AppLayout
        topBar={<TopBarBackButton title="Notification" />}
        wrapperClassName="bg-surface-L0"
        className="flex items-center justify-center"
      >
        {notFound}
      </AppLayout>
    );
  }

  const content = (
    <div
      className={
        isDesktop
          ? 'bg-surface-L0 rounded-2xl border border-border-L1 p-6 max-w-2xl'
          : 'bg-surface-L0 rounded-2xl'
      }
    >
      {/* Title */}
      <h2 className="text-lg font-semibold text-content-primary-bold mb-2">
        {notification.title}
      </h2>

      {/* Timestamp */}
      <p className="text-xs text-content-primary-subtle tracking-[0.18px] leading-normal mb-6">
        {formatDateTime(notification.createdAt)}
      </p>

      {/* Message */}
      <p className="text-sm text-content-primary-bold leading-relaxed whitespace-pre-line">
        {notification.message}
      </p>

      {/* View button if link is available */}
      {notification.link && (
        <Button
          onClick={() => {
            const { to, search } = parseNotificationLink(notification.link!);
            navigate({ to: to as any, search: search as any });
          }}
          className="mt-6 w-full"
        >
          View Details
        </Button>
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <DesktopPage>
        {desktopHeader}
        {content}
      </DesktopPage>
    );
  }

  return (
    <AppLayout
      topBar={<TopBarBackButton title="Notification" />}
      wrapperClassName="bg-surface-L0"
      className="py-4"
    >
      {content}
    </AppLayout>
  );
}
